"use client"

import { useState, useEffect } from "react"
import { Form, Button, Alert, Modal, Table, ProgressBar } from "react-bootstrap"
import axios from "axios"
import Papa from "papaparse"

const AdminProducts = () => {
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    price: "",
    originalPrice: "",
    discount: "",
    images: [],
    brand: "",
    features: "",
    description: "",
    demoVideo: "",
    specification: {
      machineType: "",
      numberOfStitches: "",
      maxStitchWidth: "",
      maxStitchLength: "",
      needleThreadingSystem: "",
      bobbinSystem: "",
      motorSpeed: "",
      presserFoot: "",
      freeArm: "",
      light: "",
      weight: "",
      warranty: "",
    },
    isTrending: false,
    isTopDiscounted: false,
  })

  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [products, setProducts] = useState([])
  const [editProductId, setEditProductId] = useState(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)
  const [showCSVModal, setShowCSVModal] = useState(false)
  const [csvFile, setCsvFile] = useState(null)
  const [csvPreview, setCsvPreview] = useState([])
  const [csvErrors, setCsvErrors] = useState([])
  const [showImageUploadModal, setShowImageUploadModal] = useState(false)
  const [imageUploadProgress, setImageUploadProgress] = useState(0)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [previewUrls, setPreviewUrls] = useState([])
  const [uploadedImages, setUploadedImages] = useState([])

  useEffect(() => {
    fetchProducts()
    fetchUploadedImages()
  }, [])

  const fetchProducts = async () => {
    try {
      const response = await axios.get("https://madhusewingmachines.com/api/products")
      setProducts(response.data.products)
    } catch (error) {
      console.error("Error fetching products:", error)
      setError("Failed to fetch products.")
    }
  }

  const fetchUploadedImages = async () => {
    try {
      console.log('Fetching uploaded images...')
      const response = await axios.get("https://madhusewingmachines.com/api/images")
      console.log('Images response:', response.data)
      if (response.data.images) {
        console.log('Setting uploaded images:', response.data.images)
        setUploadedImages(response.data.images)
      }
    } catch (error) {
      console.error("Error fetching uploaded images:", error)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    if (name.startsWith("specification.")) {
      const specField = name.split(".")[1]
      setFormData({
        ...formData,
        specification: {
          ...formData.specification,
          [specField]: value,
        },
      })
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      })
    }
  }

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files || [])
    
    // Combine existing files with new files
    const combinedFiles = [...selectedFiles, ...newFiles]
    
    // Limit to 5 files total
    const limitedFiles = combinedFiles.slice(0, 5)
    setSelectedFiles(limitedFiles)

    // Create preview URLs for all files
    const urls = limitedFiles.map((file) => URL.createObjectURL(file))
    setPreviewUrls(urls)
    
    // Clear the input so the same file can be selected again if needed
    e.target.value = ''
  }
  
  const handleRemoveImage = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index)
    const newUrls = previewUrls.filter((_, i) => i !== index)
    
    setSelectedFiles(newFiles)
    setPreviewUrls(newUrls)
  }

  const handleImageUpload = async () => {
    if (selectedFiles.length === 0) return

    const uploadFormData = new FormData()
    selectedFiles.forEach((file) => {
      uploadFormData.append("images", file)
    })

    try {
      setImageUploadProgress(50)
      const response = await axios.post("https://madhusewingmachines.com/api/images", uploadFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      setImageUploadProgress(100)
      setMessage("Images uploaded successfully!")
      setSelectedFiles([])
      setPreviewUrls([])
      fetchUploadedImages()
      setTimeout(() => setImageUploadProgress(0), 2000)
    } catch (error) {
      console.error("Error uploading images:", error)
      setError("Failed to upload images.")
      setImageUploadProgress(0)
    }
  }

  const handleImageSelect = (imageUrl) => {
    console.log('Selecting image:', imageUrl)
    // Extract just the filename from the full path if needed
    const filename = imageUrl.replace('/uploads/images/', '').replace('/uploads/', '')
    
    if (formData.images.some((img) => img.url === filename)) {
      setFormData({
        ...formData,
        images: formData.images.filter((img) => img.url !== filename),
      })
    } else if (formData.images.length < 5) {
      setFormData({
        ...formData,
        images: [...formData.images, { url: filename }],
      })
      console.log('Image added, total images:', formData.images.length + 1)
    } else {
      console.log('Maximum 5 images reached')
    }
  }

  const resetForm = () => {
    setFormData({
      name: "",
      type: "",
      price: "",
      originalPrice: "",
      discount: "",
      images: [],
      brand: "",
      features: "",
      description: "",
      demoVideo: "",
      specification: {
        machineType: "",
        numberOfStitches: "",
        maxStitchWidth: "",
        maxStitchLength: "",
        needleThreadingSystem: "",
        bobbinSystem: "",
        motorSpeed: "",
        presserFoot: "",
        freeArm: "",
        light: "",
        weight: "",
        warranty: "",
      },
      isTrending: false,
      isTopDiscounted: false,
    })
    setEditProductId(null)
    setSelectedFiles([])
    setPreviewUrls([])
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate that at least one image is selected
    if (!selectedFiles || selectedFiles.length === 0) {
      setError("Please select at least one image for the product.")
      return
    }
    
    try {
      // Create FormData to match backend expectations
      const formDataToSend = new FormData()
      
      formDataToSend.append('name', formData.name)
      formDataToSend.append('type', formData.type)
      formDataToSend.append('price', formData.price)
      formDataToSend.append('originalPrice', formData.originalPrice)
      formDataToSend.append('discount', formData.discount)
      formDataToSend.append('brand', formData.brand)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('demoVideo', formData.demoVideo || '')
      formDataToSend.append('isTrending', formData.isTrending.toString())
      formDataToSend.append('isTopDiscounted', formData.isTopDiscounted.toString())
      
      // Convert features array to JSON string
      const featuresArray = formData.features.split(",").map((feature) => feature.trim())
      formDataToSend.append('features', JSON.stringify(featuresArray))
      
      // Convert specification object to JSON string
      formDataToSend.append('specification', JSON.stringify(formData.specification))
      
      // Append image files directly
      selectedFiles.forEach((file) => {
        formDataToSend.append('images', file)
      })

      if (editProductId) {
        await axios.put(`https://madhusewingmachines.com/api/products/${editProductId}`, {
          ...formData,
          features: featuresArray,
        })
        setMessage("Product updated successfully!")
      } else {
        await axios.post("https://madhusewingmachines.com/api/products", formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        setMessage("Product added successfully!")
      }
      fetchProducts()
      resetForm()
      setShowModal(false)
    } catch (error) {
      console.error("Error details:", error.response?.data || error.message)
      setError(error.response?.data?.error || "Failed to save product. Please check the data and try again.")
    }
  }

  const handleEdit = (product) => {
    setFormData({
      ...product,
      features: product.features.join(", "),
    })
    setEditProductId(product._id)
    setShowModal(true)
  }

  const handleDeleteClick = (productId) => {
    setProductToDelete(productId)
    setShowDeleteConfirmation(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`https://madhusewingmachines.com/api/products/${productToDelete}`)
      setMessage("Product deleted successfully!")
      fetchProducts()
    } catch (error) {
      setError("Failed to delete product.")
      console.error(error)
    } finally {
      setShowDeleteConfirmation(false)
      setProductToDelete(null)
    }
  }

  const handleCSVFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setCsvFile(file)
      parseCSV(file)
    }
    e.target.value = null
  }

  const parseCSV = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: 5,
      complete: (results) => {
        setCsvPreview(results.data.slice(0, 5))

        if (results.errors.length > 0) {
          setCsvErrors(results.errors)
        } else {
          setCsvErrors([])
        }
      },
      error: (error) => {
        console.error("CSV parsing error:", error)
        setCsvErrors([{ row: 0, message: error.message }])
      },
    })
  }

  const handleCSVImport = async () => {
    if (!csvFile) {
      setError("Please select a CSV file first.")
      return
    }

    try {
      const parsePromise = new Promise((resolve, reject) => {
        Papa.parse(csvFile, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (results.errors.length > 0) {
              reject(new Error("CSV contains errors"))
              return
            }
            resolve(results.data)
          },
          error: (error) => {
            reject(error)
          },
        })
      })

      const csvData = await parsePromise

      const productsToImport = csvData.map((row) => {
        return {
          name: row.name || "",
          type: row.type || "",
          price: Number.parseFloat(row.price) || 0,
          originalPrice: Number.parseFloat(row.originalPrice) || Number.parseFloat(row.price) || 0,
          discount: row.discount || "",
          images: row.images ? row.images.split("|").map((url) => ({ url: url.trim() })) : [],
          brand: row.brand || "",
          features: row.features ? row.features.split(",").map((f) => f.trim()) : [],
          description: row.description || "",
          demoVideo: row.demoVideo || "",
          specification: {
            machineType: row.machineType || "",
            numberOfStitches: row.numberOfStitches || "",
            maxStitchWidth: row.maxStitchWidth || "",
            maxStitchLength: row.maxStitchLength || "",
            needleThreadingSystem: row.needleThreadingSystem || "",
            bobbinSystem: row.bobbinSystem || "",
            motorSpeed: row.motorSpeed || "",
            presserFoot: row.presserFoot || "",
            freeArm: row.freeArm || "",
            light: row.light || "",
            weight: row.weight || "",
            warranty: row.warranty || "",
          },
          isTrending: row.isTrending === "true",
          isTopDiscounted: row.isTopDiscounted === "true",
        }
      })

      const validProducts = productsToImport.filter((product) => {
        return product.name && product.type && !isNaN(product.price) && product.description
      })

      if (validProducts.length === 0) {
        setError("No valid products to import. Please check your CSV file.")
        return
      }

      const response = await axios.post("https://madhusewingmachines.com/api/products/import", { products: validProducts })

      setMessage(`Successfully imported ${response.data.count} products!`)
      fetchProducts()
      setShowCSVModal(false)
      setCsvFile(null)
      setCsvPreview([])
    } catch (error) {
      console.error("Import error:", error)
      setError("Failed to import products. Please check the CSV format and try again.")
    }
  }

  return (
    <div className="container">
      <h1 className="text-center">Admin Panel - Products</h1>
      {message && (
        <Alert variant="success" className="text-center">
          {message}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" className="text-center">
          {error}
        </Alert>
      )}
      {csvErrors.length > 0 && (
        <Alert variant="danger">
          <h5>CSV Errors</h5>
          <ul>
            {csvErrors.map((error, i) => (
              <li key={`csv-error-${i}`}>
                Row {error.row}: {error.message}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="mb-3">
        <Button variant="primary" onClick={() => setShowModal(true)} className="me-2">
          Add Product
        </Button>
        <Button variant="secondary" onClick={() => setShowCSVModal(true)} className="me-2">
          Import from CSV
        </Button>
        <Button variant="info" onClick={() => setShowImageUploadModal(true)}>
          Upload Images
        </Button>
      </div>

      {/* Image Upload Modal */}
      <Modal show={showImageUploadModal} onHide={() => setShowImageUploadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Product Images</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select images to upload (Max 5)</Form.Label>
            <Form.Control type="file" accept="image/*" onChange={handleFileSelect} multiple />
          </Form.Group>

          <div className="d-flex flex-wrap mb-3">
            {previewUrls.map((url, index) => (
              <div key={`preview-${index}`} className="position-relative m-2">
                <img
                  src={url || "/placeholder.svg"}
                  alt={`Preview ${index}`}
                  className="img-thumbnail"
                  style={{ width: "100px", height: "100px", objectFit: "cover" }}
                />
              </div>
            ))}
          </div>

          {imageUploadProgress > 0 && (
            <div className="mt-2">
              <ProgressBar now={imageUploadProgress} label={`${imageUploadProgress}%`} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageUploadModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleImageUpload}
            disabled={selectedFiles.length === 0 || imageUploadProgress > 0}
          >
            Upload Images
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Product Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false)
          resetForm()
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editProductId ? "Edit Product" : "Add Product"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Control type="text" name="type" value={formData.type} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Price</Form.Label>
              <Form.Control type="number" name="price" value={formData.price} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Original Price</Form.Label>
              <Form.Control type="number" name="originalPrice" value={formData.originalPrice} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Discount</Form.Label>
              <Form.Control type="text" name="discount" value={formData.discount} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Images (up to 5) - Selected: {selectedFiles.length}/5</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => document.getElementById('imageFileInput').click()}
                  disabled={selectedFiles.length >= 5}
                >
                  {selectedFiles.length === 0 ? 'Select Images' : 'Add More Images'}
                </Button>
                {selectedFiles.length > 0 && (
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => {
                      setSelectedFiles([])
                      setPreviewUrls([])
                    }}
                  >
                    Clear All
                  </Button>
                )}
              </div>
              <input
                id="imageFileInput"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                multiple
                style={{ display: 'none' }}
              />
              <Form.Text className="text-muted d-block">
                {selectedFiles.length >= 5 
                  ? "Maximum 5 images reached. Remove an image to add more." 
                  : `You can add ${5 - selectedFiles.length} more image(s). First image will be the main display image.`}
              </Form.Text>

              {previewUrls.length > 0 && (
                <>
                  <h6 className="mt-3">Selected Images Preview</h6>
                  <div className="d-flex flex-wrap mt-2">
                    {previewUrls.map((url, index) => (
                      <div key={`preview-${index}`} className="position-relative me-2 mb-2">
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          className="img-thumbnail"
                          style={{ width: "100px", height: "100px", objectFit: "cover" }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 rounded-circle p-0"
                          style={{ width: "24px", height: "24px" }}
                          onClick={() => handleRemoveImage(index)}
                          title="Remove this image"
                        >
                          ×
                        </Button>
                        {index === 0 && (
                          <div 
                            className="position-absolute bottom-0 start-0 w-100 text-center"
                            style={{ 
                              background: "rgba(0,123,255,0.8)", 
                              color: "white", 
                              fontSize: "0.7rem",
                              padding: "2px",
                              fontWeight: "bold"
                            }}
                          >
                            MAIN IMAGE
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Brand</Form.Label>
              <Form.Control type="text" name="brand" value={formData.brand} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Features (comma-separated)</Form.Label>
              <Form.Control type="text" name="features" value={formData.features} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Demo Video Link (YouTube or Video URL)</Form.Label>
              <Form.Control
                type="text"
                name="demoVideo"
                value={formData.demoVideo}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="isTrending"
                label="Is Trending"
                checked={formData.isTrending}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="isTopDiscounted"
                label="Is Top Discounted"
                checked={formData.isTopDiscounted}
                onChange={handleChange}
              />
            </Form.Group>

            <h5 className="mt-4">Specification</h5>

            <Form.Group className="mb-3">
              <Form.Label>Machine Type</Form.Label>
              <Form.Control
                type="text"
                name="specification.machineType"
                value={formData.specification.machineType}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Number of Stitches</Form.Label>
              <Form.Control
                type="text"
                name="specification.numberOfStitches"
                value={formData.specification.numberOfStitches}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Max Stitch Width</Form.Label>
              <Form.Control
                type="text"
                name="specification.maxStitchWidth"
                value={formData.specification.maxStitchWidth}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Max Stitch Length</Form.Label>
              <Form.Control
                type="text"
                name="specification.maxStitchLength"
                value={formData.specification.maxStitchLength}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Needle Threading System</Form.Label>
              <Form.Control
                type="text"
                name="specification.needleThreadingSystem"
                value={formData.specification.needleThreadingSystem}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Bobbin System</Form.Label>
              <Form.Control
                type="text"
                name="specification.bobbinSystem"
                value={formData.specification.bobbinSystem}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Motor Speed</Form.Label>
              <Form.Control
                type="text"
                name="specification.motorSpeed"
                value={formData.specification.motorSpeed}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Presser Foot</Form.Label>
              <Form.Control
                type="text"
                name="specification.presserFoot"
                value={formData.specification.presserFoot}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Free Arm</Form.Label>
              <Form.Control
                type="text"
                name="specification.freeArm"
                value={formData.specification.freeArm}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Light</Form.Label>
              <Form.Control
                type="text"
                name="specification.light"
                value={formData.specification.light}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Weight</Form.Label>
              <Form.Control
                type="text"
                name="specification.weight"
                value={formData.specification.weight}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Warranty</Form.Label>
              <Form.Control
                type="text"
                name="specification.warranty"
                value={formData.specification.warranty}
                onChange={handleChange}
              />
            </Form.Group>

            <Button variant="primary" type="submit" className="mt-3">
              {editProductId ? "Update Product" : "Add Product"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* CSV Import Modal */}
      <Modal show={showCSVModal} onHide={() => setShowCSVModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Import Products from CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <strong>Note:</strong> Before importing, make sure you've uploaded all required images using the "Upload
            Images" button. In your CSV, reference images by their filenames separated by "|" (e.g.,
            "image1.jpg|image2.jpg").
          </Alert>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>CSV File</Form.Label>
              <Form.Control
                key={csvFile ? csvFile.name : "csv-input"}
                type="file"
                accept=".csv"
                onChange={handleCSVFileChange}
                required
              />
              <Form.Text className="text-muted">
                CSV should include headers matching product fields. Download{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    const headers = [
                      "name",
                      "type",
                      "price",
                      "originalPrice",
                      "discount",
                      "images",
                      "brand",
                      "features",
                      "description",
                      "demoVideo",
                      "machineType",
                      "numberOfStitches",
                      "maxStitchWidth",
                      "maxStitchLength",
                      "needleThreadingSystem",
                      "bobbinSystem",
                      "motorSpeed",
                      "presserFoot",
                      "freeArm",
                      "light",
                      "weight",
                      "warranty",
                      "isTrending",
                      "isTopDiscounted",
                    ].join(",")

                    const sampleData = [
                      '"Sample Sewing Machine","Industrial",29999,34999,"14%","product1.jpg|product2.jpg","Janome","Automatic needle threader,Free arm","This is a sample description","https://www.youtube.com/watch?v=example","Electronic","200","5mm","4mm","Automatic","Top Drop-in","850 spm","Snap-on","Yes","LED","7.3kg","2 Years","true","false"',
                    ].join("\n")

                    const csvContent = [headers, sampleData].join("\n")
                    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
                    const url = URL.createObjectURL(blob)
                    const link = document.createElement("a")
                    link.href = url
                    link.download = "sample-products.csv"
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                  }}
                >
                  sample CSV template
                </a>
                .
              </Form.Text>
            </Form.Group>

            {csvPreview.length > 0 && (
              <>
                <h5>Preview (first 5 rows)</h5>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      {Object.keys(csvPreview[0]).map((key) => (
                        <th key={`csv-header-${key}`}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.map((row, i) => (
                      <tr key={`csv-row-${i}-${row.name || i}`}>
                        {Object.values(row).map((val, j) => (
                          <td key={`csv-cell-${i}-${j}`}>{val}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {csvErrors.length > 0 && (
              <Alert variant="danger">
                <h5>CSV Errors</h5>
                <ul>
                  {csvErrors.map((error, i) => (
                    <li key={`csv-error-${i}`}>
                      Row {error.row}: {error.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCSVModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCSVImport} disabled={!csvFile}>
            Import Products
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirmation} onHide={() => setShowDeleteConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this product?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Products Table */}
      <h3 className="mt-5">Products List</h3>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Price</th>
            <th>Discount</th>
            <th>Brand</th>
            <th>Trending</th>
            <th>Images</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr key={product._id}>
              <td>{product.name}</td>
              <td>{product.type}</td>
              <td>₹{product.price}</td>
              <td>{product.discount}</td>
              <td>{product.brand}</td>
              <td>{product.isTrending ? "Yes" : "No"}</td>
              <td>
                {product.images.slice(0, 3).map((image, idx) => (
                  <img
                    key={`${product._id}-img-${idx}`}
                    src={`https://madhusewingmachines.com/uploads/${image.url}`}
                    alt={`${product.name} ${idx}`}
                    className="img-thumbnail me-2"
                    style={{ width: "50px", height: "50px", objectFit: "cover" }}
                  />
                ))}
                {product.images.length > 3 && `+${product.images.length - 3} more`}
              </td>
              <td>
                <Button variant="warning" size="sm" onClick={() => handleEdit(product)} className="me-2">
                  Edit
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleDeleteClick(product._id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

export default AdminProducts
